/**
 * The members and invitations page (route `/members`; Feature: user-and-data-management;
 * **Requirement 6**; Requirements 5.1, 5.2, 5.5, 5.7, 7.1, 7.3, 7.4, 7.5).
 *
 * Built on the `ChecksListPage` / `AlertRulesListPage` conventions — the same `LoadState` shape, the
 * same `checks-table`, the same "rows are never mutated locally, so a failed write leaves the
 * pre-request view intact and the list re-reads only after the API confirms" rule. What is specific to
 * this page:
 *
 *  - **The page is a read.** Every other write surface in the dashboard hides itself from a viewer;
 *    this one does not, because Requirement 6's premise is that *knowing who can see your data is not
 *    privileged*. A viewer sees both tables in full and no write affordance at all.
 *  - **Two reads, one load state.** Members and invitations are separate collections but a single
 *    question, so they are fetched together and a failure of either is one error panel rather than a
 *    half-rendered page.
 *  - **A member is an opaque identifier.** Req 2.3 keeps email and name in Cognito, so `MemberDto`
 *    carries only a `sub`. The table shows it verbatim, with the reader's own row marked from the `sub`
 *    claim on their token. That is honest, and it is also the page's weakest point: a *pending*
 *    invitation names an email, so it answers "who", while an *accepted* one becomes a `sub` and stops
 *    answering it. Requirement 6 is about knowing who can see your data, and an opaque identifier only
 *    half-answers that. Closing it needs the API to resolve emails from Cognito at read time, which
 *    Req 2.2 permits (read from Cognito, persist nothing) — it is not something the SPA can fix.
 *  - **Expiry is derived, not read.** There is no `expired` wire state; a stale invitation is still
 *    `pending` with a past `expiresAtEpoch` (Req 5.5), so the badge is computed against now.
 *  - **Role change writes nothing until it is confirmed.** The row `<select>` stays bound to the
 *    member's persisted role and a change only opens the dialog, so an accidental select is not an
 *    escalation and a failed write leaves nothing locally out of step with the server. Promotion to a
 *    write role carries {@link WriteAccessWarning}, because granting write by promotion has exactly the
 *    consequences (Req 6.2, 6.5) that granting it by invitation does.
 *  - **Removal states its limits first, and reports what it did afterwards** (Req 7.3, 7.4, 7.5, 7.7,
 *    7.8, 7.10): the former member's checks stay with the account, anything they already read is out of
 *    reach, and their API keys go with the membership **only where the platform can attribute them** —
 *    a key recording no minter survives and is named back for a human to judge. The confirmation states
 *    that rule; the post-removal notice is generated from the response, in {@link RemovalNotice}. An
 *    earlier version of this comment, and of both pieces of copy, claimed the keys were simply revoked.
 *
 * The last-admin refusal (Req 5.7) is the API's `409 conflict`, and its message is surfaced verbatim
 * in the dialog rather than paraphrased — the platform knows why it refused and the dashboard does not.
 */

import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { ApiError } from '../api/client';
import type { InvitationDto, MemberDto, MemberRole, RemoveMemberResponse } from '../api/types';
import { MEMBER_ROLES, roleCanWrite } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { identityFromUser } from '../auth/identity';
import { useCanAdministerMembers } from '../auth/use-can-write';
import { InvitationStatusBadge, type InvitationDisplayStatus } from '../components/Badges';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { InfoTooltip } from '../components/InfoTooltip';
import { InviteMemberForm } from '../components/InviteMemberForm';
import { ROLE_LABELS, WriteAccessWarning } from '../components/SharingDisclosure';
import { formatEpochSeconds } from '../lib/format';
import { useWrite } from '../lib/use-write';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; members: MemberDto[]; invitations: InvitationDto[] };

/** The one write awaiting confirmation. A union rather than three flags: only one dialog is open. */
type PendingAction =
  | { kind: 'remove'; member: MemberDto }
  | { kind: 'role'; member: MemberDto; nextRole: MemberRole }
  | { kind: 'revoke'; invitation: InvitationDto };

/**
 * What the page announces after a confirmed write.
 *
 * A union rather than a `string`, and that is the whole point of Requirement 7.8: a removal's notice is
 * **generated from the response** — which keys were revoked, which survived, which failed — so the copy
 * cannot drift from what the platform did. Holding the response rather than a pre-rendered sentence is
 * what makes that structural instead of a convention somebody has to remember. The other three writes
 * (invite, role change, invitation revoke) report a single fact and stay strings.
 */
type PageNotice =
  | { kind: 'message'; text: string }
  | { kind: 'removal'; result: RemoveMemberResponse };

/**
 * When a member joined. `acceptedAtEpoch` is the accurate answer for a membership created by an
 * invitation; a founding membership has none, so the record's own creation time stands in.
 */
function joinedAt(member: MemberDto): number {
  return member.acceptedAtEpoch ?? member.createdAtEpoch;
}

/** An invitation's displayed status, with expiry derived from the timestamp (Req 5.5). */
function invitationStatus(
  invitation: InvitationDto,
  nowEpoch: number,
): InvitationDisplayStatus {
  if (invitation.state !== 'pending') return invitation.state;
  return invitation.expiresAtEpoch <= nowEpoch ? 'expired' : 'pending';
}

/** An absent optional value is stated, never rendered blank. */
function orDash(value: string | undefined): string {
  return value !== undefined && value.length > 0 ? value : '—';
}

/** `1 API key` / `3 API keys`, so a count an admin acts on cannot disagree with its own noun. */
function keyCount(ids: readonly string[]): string {
  return `${String(ids.length)} API ${ids.length === 1 ? 'key' : 'keys'}`;
}

/** The verb that agrees with {@link keyCount}. Two words, and they are the two that read wrong most. */
function keysWere(ids: readonly string[]): string {
  return ids.length === 1 ? 'was' : 'were';
}

export function MembersPage(): JSX.Element {
  const client = useApiClient();
  // Req 5.6: managing membership is admin-only, narrower than the Check-write gate.
  const canWrite = useCanAdministerMembers();
  const auth = useAuth();
  const { userSub } = identityFromUser(auth.user);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<PendingAction | undefined>(undefined);
  const [notice, setNotice] = useState<PageNotice | undefined>(undefined);
  // Two runners: the invite form and the dialogs fail independently, and the dialogs are mutually
  // exclusive so they can share one.
  const invite = useWrite();
  const membership = useWrite();

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    Promise.all([client.listMembers(), client.listInvitations()])
      .then(([memberList, invitationList]) => {
        if (active) {
          setState({
            status: 'loaded',
            members: memberList.members,
            invitations: invitationList.invitations,
          });
        }
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Failed to load members';
        const isAuthError = error instanceof ApiError && error.isAuthError;
        setState({ status: 'error', message, isAuthError });
      });
    return () => {
      active = false;
    };
  }, [client, reloadToken]);

  /** Re-read both collections. Only ever called after the API confirmed a write. */
  function reload(): void {
    setReloadToken((token) => token + 1);
  }

  function openAction(next: PendingAction): void {
    membership.reset();
    setNotice(undefined);
    setPending(next);
  }

  function closeAction(): void {
    membership.reset();
    setPending(undefined);
  }

  function confirmPending(action: PendingAction): void {
    if (action.kind === 'remove') {
      void membership.run(
        () => client.removeMember(action.member.userSub),
        // The response, not a sentence: the notice is derived from it in {@link RemovalNotice}. A `207`
        // arrives here rather than as an error — it is in the 2xx class precisely so a partial success
        // reaches this page instead of reading as a failed removal that in fact happened (Req 7.10).
        (response) => {
          setPending(undefined);
          setNotice({ kind: 'removal', result: response });
          reload();
        },
      );
      return;
    }
    if (action.kind === 'role') {
      void membership.run(
        () => client.updateMember(action.member.userSub, { role: action.nextRole }),
        (response) => {
          setPending(undefined);
          setNotice({
            kind: 'message',
            text: `${response.member.userSub} is now ${response.member.role}.`,
          });
          reload();
        },
      );
      return;
    }
    void membership.run(
      () => client.revokeInvitation(action.invitation.inviteId),
      () => {
        setPending(undefined);
        setNotice({
          kind: 'message',
          text: `Revoked the invitation to ${action.invitation.email}.`,
        });
        reload();
      },
    );
  }

  return (
    <section>
      <div className="section-header">
        {/* The hint annotates the heading rather than an action, so it is grouped with it — a bare
            `i` on the far side of a `space-between` header would annotate nothing. It is a one-line
            hint and nothing more: the Requirement 6 disclosure is prominent copy on the form below,
            because 6.1 requires it without a documentation visit and a tooltip is a documentation
            visit with extra steps. This is the part a viewer, who never sees the form, still needs. */}
        <div className="section-action-with-hint">
          <h1>Members</h1>
          <InfoTooltip label="About account members">
            Sharing is account-wide: a member sees every check in this account, including captured
            response bodies and traces. There is no per-check sharing.
          </InfoTooltip>
        </div>
      </div>

      {notice !== undefined ? (
        <div role="status" className="muted" data-testid="page-notice">
          {notice.kind === 'message' ? <p>{notice.text}</p> : <RemovalNotice result={notice.result} />}
        </div>
      ) : null}

      <MembersBody
        state={state}
        canWrite={canWrite}
        currentUserSub={userSub}
        onChangeRole={(member, nextRole) => openAction({ kind: 'role', member, nextRole })}
        onRemove={(member) => openAction({ kind: 'remove', member })}
        onRevoke={(invitation) => openAction({ kind: 'revoke', invitation })}
      />

      {canWrite ? (
        <>
          <h2>Invite a member</h2>
          <InviteMemberForm
            submitting={invite.state.submitting}
            fieldErrors={invite.state.fieldErrors}
            errorMessage={invite.state.errorMessage}
            onSubmit={(payload) =>
              void invite.run(
                () => client.createInvitation(payload),
                (response) => {
                  setNotice({
                    kind: 'message',
                    text: `Invited ${response.invitation.email} as ${response.invitation.role}. They get access only once they accept, and the invitation expires ${formatEpochSeconds(response.invitation.expiresAtEpoch)}.`,
                  });
                  reload();
                },
              )
            }
          />
        </>
      ) : (
        <p role="note" className="muted" data-testid="read-only-notice">
          Your role has read-only access, so members cannot be invited, changed, or removed from the
          dashboard. Everyone with access to this account is listed above.
        </p>
      )}

      {pending !== undefined ? (
        <MembershipDialog
          action={pending}
          currentUserSub={userSub}
          busy={membership.state.submitting}
          errorMessage={membership.state.errorMessage}
          onConfirm={() => confirmPending(pending)}
          onCancel={closeAction}
        />
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* What the removal actually did                                               */
/* -------------------------------------------------------------------------- */

/**
 * The post-removal notice, **generated from the response** (Req 7.8, 7.10; Design: §Requirement 7.3 —
 * Decision 8). Same discipline as the `adopted` / `previousOrigin` notice on the Check pages: the copy is
 * a function of what came back, so it cannot drift from what the platform did.
 *
 * It replaces a single unconditional sentence — *"their API keys for this account are revoked"* — which
 * was false when nothing revoked them and would still be an overstatement now, because a key the platform
 * cannot attribute to the departing member is deliberately left alive (Req 7.7).
 *
 * **Each of the three sentences renders only when its list is non-empty**, and that is a requirement
 * rather than tidiness: an Account whose every key carries a minter must not be warned about a residue
 * caveat that did not apply to it, or the caveat becomes noise and stops being read in the Accounts where
 * it does apply.
 *
 * **The failed-keys sentence does not offer a retry, because there is none on this route.** Repeating the
 * removal answers `404` — the membership row is already gone, so `getMembership` misses before the key
 * sweep is reached — which writes nothing and re-attempts nothing. The only working recovery is revoking
 * each named key on the API keys page, so that is what the copy says. Telling an admin to try the removal
 * again would put a second false claim on the page this exists to stop lying.
 */
function RemovalNotice({ result }: { result: RemoveMemberResponse }): JSX.Element {
  const revoked = result.revokedKeyIds;
  const unattributed = result.unattributedKeyIds;
  // Absent means "no failure", which is the shape the API guarantees: `failedKeyIds` is present only
  // when non-empty, never `[]`, so there is no empty array to mistake for a fact nobody checked.
  const failed = result.failedKeyIds ?? [];

  return (
    <>
      {/* Req 7.4 — what a removal cannot reach, said again after the fact and not only before it. */}
      <p>
        Removed {result.userSub}. Their access stops at their next request. Anything they already read,
        exported, or copied is outside the platform&apos;s reach.
      </p>
      {revoked.length > 0 ? (
        <p data-testid="removal-revoked-keys">
          Revoked {keyCount(revoked)} this member minted in this account: {revoked.join(', ')}.
        </p>
      ) : null}
      {unattributed.length > 0 ? (
        <p data-testid="removal-unattributed-keys">
          {keyCount(unattributed)} in this account {keysWere(unattributed)} not revoked, because the
          platform has no record of who minted {unattributed.length === 1 ? 'it' : 'them'}:{' '}
          {unattributed.join(', ')}. Judge each one and revoke it on the API keys page if it belonged to
          this member.
        </p>
      ) : null}
      {failed.length > 0 ? (
        <p data-testid="removal-failed-keys">
          <strong>
            {keyCount(failed)} this member minted could not be revoked and{' '}
            {failed.length === 1 ? 'is' : 'are'} still usable: {failed.join(', ')}. Revoke each one on the
            API keys page to close that access.
          </strong>
        </p>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* The two tables                                                              */
/* -------------------------------------------------------------------------- */

function MembersBody({
  state,
  canWrite,
  currentUserSub,
  onChangeRole,
  onRemove,
  onRevoke,
}: {
  state: LoadState;
  canWrite: boolean;
  currentUserSub: string | undefined;
  onChangeRole: (member: MemberDto, nextRole: MemberRole) => void;
  onRemove: (member: MemberDto) => void;
  onRevoke: (invitation: InvitationDto) => void;
}): JSX.Element {
  if (state.status === 'loading') {
    return (
      <p role="status" className="muted">
        Loading members…
      </p>
    );
  }

  if (state.status === 'error') {
    return (
      <div role="alert" className="error-panel">
        <p>{state.isAuthError ? 'Your session expired. Please sign in again.' : 'Could not load members.'}</p>
        <p className="muted">{state.message}</p>
      </div>
    );
  }

  // Only outstanding invitations are listed: an accepted one is already a row in the members table,
  // and a revoked one grants nothing. The members table is the answer to "who can see this account".
  const outstanding = state.invitations.filter((invitation) => invitation.state === 'pending');
  const nowEpoch = Math.floor(Date.now() / 1000);

  return (
    <>
      {state.members.length === 0 ? (
        <p className="muted">This account has no members.</p>
      ) : (
        <table className="checks-table" aria-label="Members">
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Role</th>
              <th scope="col">Joined</th>
              <th scope="col">Invited by</th>
              {canWrite ? <th scope="col">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {state.members.map((member) => {
              const isSelf = currentUserSub !== undefined && member.userSub === currentUserSub;
              return (
                <tr key={member.userSub}>
                  <td>
                    <code>{member.userSub}</code>
                    {isSelf ? <span className="self-marker"> (you)</span> : null}
                  </td>
                  <td>{member.role}</td>
                  <td>{formatEpochSeconds(joinedAt(member))}</td>
                  <td>
                    <code>{orDash(member.invitedBy)}</code>
                  </td>
                  {canWrite ? (
                    <td className="row-actions">
                      <select
                        aria-label={`Role for ${member.userSub}`}
                        // Bound to the persisted role: selecting only opens the confirmation, so
                        // nothing is written and nothing drifts from the server until it is confirmed.
                        value={member.role}
                        onChange={(event) => onChangeRole(member, event.target.value as MemberRole)}
                      >
                        {MEMBER_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {ROLE_LABELS[role]}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="danger"
                        aria-label={`Remove ${member.userSub}`}
                        onClick={() => onRemove(member)}
                      >
                        Remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <h2>Pending invitations</h2>
      {outstanding.length === 0 ? (
        <p className="muted">No invitations are outstanding.</p>
      ) : (
        <table className="checks-table" aria-label="Pending invitations">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Sent</th>
              <th scope="col">Expires</th>
              <th scope="col">Invited by</th>
              {canWrite ? <th scope="col">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {outstanding.map((invitation) => (
              <tr key={invitation.inviteId}>
                <td>{invitation.email}</td>
                <td>{invitation.role}</td>
                <td>
                  <InvitationStatusBadge status={invitationStatus(invitation, nowEpoch)} />
                </td>
                <td>{formatEpochSeconds(invitation.createdAtEpoch)}</td>
                <td>{formatEpochSeconds(invitation.expiresAtEpoch)}</td>
                <td>
                  <code>{invitation.invitedBy}</code>
                </td>
                {canWrite ? (
                  <td className="row-actions">
                    <button
                      type="button"
                      className="danger"
                      aria-label={`Revoke invitation for ${invitation.email}`}
                      onClick={() => onRevoke(invitation)}
                    >
                      Revoke
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Confirmations                                                               */
/* -------------------------------------------------------------------------- */

function MembershipDialog({
  action,
  currentUserSub,
  busy,
  errorMessage,
  onConfirm,
  onCancel,
}: {
  action: PendingAction;
  currentUserSub: string | undefined;
  busy: boolean;
  errorMessage?: string;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  if (action.kind === 'revoke') {
    return (
      <ConfirmDialog
        title="Revoke invitation"
        message={`Revoking the invitation to ${action.invitation.email} means it can no longer be accepted. Nothing was shared with them, because an invitation grants access only on acceptance.`}
        confirmLabel="Revoke invitation"
        busy={busy}
        errorMessage={errorMessage}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  }

  if (action.kind === 'role') {
    const escalating = roleCanWrite(action.nextRole) && !roleCanWrite(action.member.role);
    return (
      <ConfirmDialog
        title="Change role"
        message={`Change ${action.member.userSub} from ${action.member.role} to ${action.nextRole}? The new role applies to their next request.`}
        confirmLabel="Change role"
        busy={busy}
        errorMessage={errorMessage}
        onConfirm={onConfirm}
        onCancel={onCancel}
      >
        {/* Promoting a reader to a writer grants exactly what Req 6.2 and 6.5 warn about, so the
            warning follows the privilege rather than the route that granted it. */}
        {escalating ? (
          <div className="sharing-disclosure">
            <WriteAccessWarning role={action.nextRole} />
          </div>
        ) : null}
      </ConfirmDialog>
    );
  }

  const isSelf = currentUserSub !== undefined && action.member.userSub === currentUserSub;
  return (
    <ConfirmDialog
      title="Remove member"
      message={`Remove ${action.member.userSub} from this account? Their access stops at their next request, with no cache window.`}
      confirmLabel="Remove member"
      busy={busy}
      errorMessage={errorMessage}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <div role="note" className="adoption-notice" data-testid="removal-notice">
        {/* Req 7.5 — the point a sharer is most likely to get wrong in the opposite direction: they
            expect removal to take the member's work with them. It does not. */}
        <p>
          Checks, alert rules, and drafts this member authored <strong>stay with the account</strong>.
          Removing them deletes none of the account&apos;s monitoring data.
        </p>
        {/* Req 7.3, 7.7, 7.8 — this speaks BEFORE the removal, so it cannot name keys. It therefore
            states the rule in the shape the rule actually has, rather than the shape a reader would
            prefer. It replaces "Any API keys they minted in this account are revoked along with the
            membership", which claimed a control the platform does not have: attribution is a stored
            fact, and a key that carries none is left alive on purpose (failing closed would silently
            break a colleague's live integration). Longer than the sentence it replaces, and honest. */}
        <p>
          API keys this account can attribute to them — the ones recorded as minted by them — are{' '}
          <strong>revoked</strong> with the membership. A key that records no minter, including every key
          issued before the platform started recording one, is <strong>not</strong> revoked: those are
          listed after the removal so you can judge each one and revoke it yourself.
        </p>
        {/* Req 7.4 — the honest counterpart to the non-retroactive-revocation promise made at
            invitation time. */}
        <p className="muted">
          Removal is not retroactive. Data they have already read, exported, or copied — including
          captured response bodies and traces — is outside the platform&apos;s reach.
        </p>
        {isSelf ? (
          <p className="muted" data-testid="self-removal-notice">
            This is your own membership. Removing it ends your access to this account.
          </p>
        ) : null}
      </div>
    </ConfirmDialog>
  );
}
